export const groupBy = (xs, key) =>
  xs.reduce(function (rv, x) {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});

export const onlyUnique = (value, index, array) => array.indexOf(value) === index;

export const objectToFormData = (obj, formData = new FormData(), namespace = "") => {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      let propName = namespace ? `${namespace}[${key}]` : key;

      if (Array.isArray(obj[key])) {
        // Handle array of objects or primitives
        obj[key].forEach((item, index) => {
          if (item instanceof File) {
            // Check if the item is a File instance
            formData.append(`${propName}[${index}]`, item);
          } else if (typeof item === "object" && item !== null) {
            // If item is an object, call recursively
            objectToFormData(item, formData, `${propName}[${index}]`);
          } else {
            // If item is a primitive, append it
            formData.append(`${propName}[${index}]`, item);
          }
        });
      } else if (obj[key] instanceof File) {
        // Check if obj[key] is a File instance
        formData.append(propName, obj[key]);
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        // Handle nested objects
        objectToFormData(obj[key], formData, propName);
      } else {
        // Handle primitive types
        formData.append(propName, obj[key]);
      }
    }
  }
  return formData;
};

export const searchMenu = (array = [], searchTerm = "") => {
  if (!searchTerm) return array;

  const lowerSearchTerm = searchTerm.toLowerCase();

  const recursiveFilter = (items) => {
    return items
      .map((item) => {
        if (!item.submenu || item.submenu.length === 0) {
          return item.name.toLowerCase().includes(lowerSearchTerm) ? item : null;
        }

        const filteredSubmenus = recursiveFilter(item.submenu);
        const nameMatches = item.name.toLowerCase().includes(lowerSearchTerm);
        const hasMatchingSubmenus = filteredSubmenus.length > 0;

        if (!nameMatches && !hasMatchingSubmenus) {
          return null;
        }

        if (nameMatches) {
          return {
            ...item,
            submenu: item.submenu,
            isOpen: true,
          };
        }

        return {
          ...item,
          submenu: filteredSubmenus,
          isOpen: true,
          subMenuFilteredDown: true,
        };
      })
      .filter(Boolean);
  };

  return recursiveFilter(array);
};

export const isSVGString = (str) => {
  const svgRegex = /<svg(.*?)<\/svg>/i;
  return svgRegex.test(str);
};

export const getCookies = () => {
  const cookieData = document.cookie;
  if (!cookieData) return {};

  const cookiesArray = cookieData.split(";");
  const cookies = cookiesArray.reduce((cookies, cookie) => {
    const [key, value] = cookie.trim().split("=");
    cookies[key] = decodeURIComponent(value);
    return cookies;
  }, {});

  return cookies;
};

export const formatCurrency = (amount, currency = "USD", locale = "en-US") => {
  // Create a number formatter instance based on locale and currency code
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2, // Ensure 2 decimal places
  });

  // Format the amount using the formatter
  return formatter.format(amount);
};

export const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const clearCookies = () => {
  const cookies = getCookies();
  const cookieKeys = Object.keys(cookies);

  // Iterate through each cookie key and set its expiration date to the past
  cookieKeys.forEach((key) => {
    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
};

export function extractAndRemoveScripts(htmlDoc) {
  // Create a new DOM parser
  const parser = new DOMParser();
  // Parse the HTML document
  const doc = parser.parseFromString(htmlDoc, "text/html");

  // Get all script elements
  const scripts = doc.getElementsByTagName("script");
  const extractedScripts = [];

  // Iterate through script tags in reverse to avoid live NodeList issues
  for (let i = scripts.length - 1; i >= 0; i--) {
    // Add script outerHTML to the array
    extractedScripts.push(scripts[i].outerHTML);
    // Remove the script element from the document
    scripts[i].parentNode.removeChild(scripts[i]);
  }

  // Serialize the document back to a string
  const serializer = new XMLSerializer();
  const modifiedHtml = serializer.serializeToString(doc);

  return { extractedScripts, modifiedHtml };
}

export const transpileES6ToES5 = (code) => {
  // Regular expression to match ES6 let and const declarations
  const letConstRegex = /\b(let|const)\s+/g;

  // Replace let and const with var
  const transpiledCode = code.replace(letConstRegex, "var ");

  return transpiledCode;
};

export const getMinuteDifference = (date1, date2) => {
  const diffInMs = Math.abs(date2 - date1);
  const minutes = Math.floor(diffInMs / (1000 * 60));
  return minutes;
};

export const setDrawerCookie = (workspaceId, path) => {
  const cookieName = "drawerOpen";
  const cookieValue = `${workspaceId}:${path}:true`;
  document.cookie = `${cookieName}=${cookieValue}; path=${path}; max-age=${60 * 60 * 24 * 365}`; // Store for 1 year
};

export const checkDrawerCookie = (workspaceId, path) => {
  const cookieName = "drawerOpen";
  const cookies = document.cookie.split("; ");
  const drawerCookie = cookies.find((cookie) => cookie.startsWith(`${cookieName}=`));

  if (drawerCookie) {
    const cookieValue = drawerCookie.split("=")[1];
    const [cookieWorkspaceId, cookiePath, cookieState] = cookieValue.split(":");
    return cookieWorkspaceId === workspaceId && cookiePath === path && cookieState === "true";
  }

  return false;
};

const COOKIE_NAME = "a2hsDismissedWS";

const getAllDismissedWorkspaces = () => {
  const pair = document.cookie.split("; ").find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!pair) return [];

  try {
    const cookieValue = decodeURIComponent(pair.split("=")[1]);
    return cookieValue ? cookieValue.split(",").filter(Boolean) : [];
  } catch {
    return [];
  }
};

export const clearA2HSDismissedForWS = (workspaceId) => {
  const existingDismissed = getAllDismissedWorkspaces();
  const updatedDismissed = existingDismissed.filter((ws) => String(ws) !== String(workspaceId));

  if (updatedDismissed.length === 0) {
    document.cookie = `${COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } else {
    const value = encodeURIComponent(updatedDismissed.join(","));
    document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
  }
};

export const setA2HSDismissedForWS = (workspaceId) => {
  const existingDismissed = getAllDismissedWorkspaces();
  const updatedDismissed = [...new Set([...existingDismissed, String(workspaceId)])];
  const value = encodeURIComponent(updatedDismissed.join(","));
  document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
};

export const isA2HSDismissedForWS = (workspaceId) => {
  const dismissedWorkspaces = getAllDismissedWorkspaces();
  return dismissedWorkspaces.includes(String(workspaceId));
};

export const allowedScriptTypes = ["text/javascript", "application/javascript", "application/ecmascript"];
